import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import s3Service from '../services/s3Service';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const SearchResults = () => {
  const navigate = useNavigate();
  const queryParams = useQuery();
  const query = queryParams.get('q') || '';
  const pageParam = parseInt(queryParams.get('page') || '1', 10);

  const [results, setResults] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(pageParam);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [totalResults, setTotalResults] = useState(0);
  const [isContactOpen, setIsContactOpen] = useState(false);
  useEffect(() => {
    const fetchResults = async () => {
      setIsSearching(true);
      setError(null);

      try {
        const { results, totalPages: pages, totalResults } = await s3Service.searchContent(query, currentPage, 20);
        setResults(results);
        setTotalPages(pages);
        setTotalResults(totalResults);
      } catch (err) {
        console.error(err);
        setError('חיפוש נכשל, נסה שוב');
      } finally {
        setIsSearching(false);
      }
    };

    if (query) fetchResults();
  }, [query, currentPage]);

  const handlePageClick = (page) => {
    setCurrentPage(page);
    navigate(`/search?q=${encodeURIComponent(query)}&page=${page}`);
  };

  const handleResultClick = (result) => {
    const fileName = result.title.endsWith('.html') ? result.title : result.title + '.html';
    navigate(
      `/book/${result.book.toLowerCase()}/parsha/${encodeURIComponent(result.parasha)}/file/${encodeURIComponent(fileName)}`
    );
  };
  const translateBookName = (bookName) => {
    switch (bookName) {
      case 'Bereshit':
        return 'בראשית';
      case 'Shemot':
        return 'שמות';
      case 'Vayikra':
        return 'ויקרא';
      case 'Bamidbar':
        return 'במדבר';
      case 'Devarim':
        return 'דברים';
      case 'Holidays':
        return 'חגים';
      default:
        return bookName;
    }
  };
  const handleGoBack = () => {
    navigate(-1);
  }

  return (
  <div className="min-h-screen flex flex-col">

    {/* TOP NAV BAR */}
    <div dir="rtl" className="w-full py-4 px-6 flex justify-between items-center">
      
      {/* Contact */}
      <div className="relative">
        <button 
          onClick={() => setIsContactOpen(!isContactOpen)}
          className="flex items-center gap-2 text-amber-300 hover:text-amber-400 transition font-semibold text-base"
        >
          <span>צור קשר</span>
          <span className={`text-[11px] transition-transform ${isContactOpen ? 'rotate-180' : ''}`}>▼</span>
        </button>

        {isContactOpen && (
          <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl p-2 z-50 text-gray-800 border border-gray-200">
            <a href="mailto:tora2338@gmail.com" className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition">
              <span className="text-xl">📧</span>
              <div className="flex flex-col text-right">
                <span className="font-bold text-sm">דברו איתנו במייל</span>
                <span className="text-[11px] text-gray-500">להערות, הארות, לקבלת הדף השבועי במייל הקדשה לעילוי נשמת וכו׳</span>
              </div>
            </a>
            <a  
              href="https://links.payboxapp.com/iBta1etlyWb" 
              target="_blank" 
              rel="noreferrer" 
              className="flex items-center gap-3 p-3 hover:bg-orange-50 rounded-lg transition border-t mt-1"
            >
              <span className="text-xl">💰</span>
              <div className="flex flex-col text-right">
                <span className="font-bold text-sm text-orange-700">תרומה בפייבוקס</span>
                <span className="text-[11px] text-gray-500">עזרו לנו לגדול</span>
              </div>
            </a>
          </div>
        )}
      </div>

      {/* Logo / Title center */}
      <div className="absolute left-1/2 -translate-x-1/2 text-center hidden md:block">
        <span className="text-amber-300 font-bold text-lg md:text-xl tracking-wide">📖 הלכה בפרשה</span>
      </div>

      {/* App Link */}
      <a 
        href="https://play.google.com/store/apps/details?id=com.halachaparasha.app" 
        target="_blank" 
        rel="noreferrer" 
        className="font-semibold text-base text-amber-300 hover:text-amber-400 transition"
      >
        מעבר לאפליקציה 📱
      </a>
    </div>

    {/* Page Content */}
    <div className="space-y-6 max-w-4xl mx-auto px-6 mt-10 w-full" dir="rtl">

      {/* Back Button */}
      <div className="mb-2 w-full">
        <button
          onClick={() => handleGoBack()}
          className="flex items-center gap-1 text-amber-600 hover:text-amber-800 transition font-semibold text-lg md:text-xl ml-0 mr-auto"
        >
          ← חזור
        </button>
      </div>

      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-amber-300 text-center">
        <h3 className="text-lg text-gray-600 font-normal">תוצאות חיפוש</h3>
        <h2 className="text-4xl font-bold text-gray-900 mt-2">{query || '...'}</h2>
        <div className="w-16 h-[2px] bg-gray-300 my-4 mx-auto"></div>
        {totalResults > 0 && (
          <p className="text-base text-gray-700">
            {totalResults} תוצאות נמצאו
          </p>
        )}
      </div>

      {/* Loading Spinner */}
      {isSearching && (
        <div className="flex justify-center my-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-amber-400 border-b-4 border-gray-200"></div>
        </div>
      )}

      {/* Error Message */}
      {error && <p className="text-center text-red-500">{error}</p>}

      {/* Search Results */}
      {!isSearching && results.length > 0 && (
        <div className="space-y-4">
          {results.map((result, index) => (
            <button
              key={index}
              onClick={() => handleResultClick(result)}
              className="w-full bg-white rounded-2xl p-5 shadow-lg hover:shadow-2xl transition-shadow duration-300 flex items-center justify-between border border-amber-200 hover:border-amber-300 hover:scale-[1.03] transform"
            >
              <div className="flex-1 text-right">
                <p className="text-lg font-semibold text-gray-900 leading-snug">
                  {result.title.replace(/\.html$/, '')}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {translateBookName(result.book)} - {result.parasha}
                </p>
              </div>
            </button>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => handlePageClick(p)}
                  className={`px-3 py-1 border rounded ${
                    p === currentPage
                      ? 'bg-gray-300'
                      : 'bg-white hover:bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* No results */}
      {!isSearching && results.length === 0 && query && !error && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center">
          <p className="text-gray-700 font-medium">
            לא נמצאו תוצאות עבור "{query}"
          </p>
        </div>
      )}
    </div>

  </div>
);

};

export default SearchResults;